import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import {
  Wallet, Plus, UploadSimple, Trash, TrendUp, TrendDown, Scales,
  Warning, FloppyDisk, X, ChartBar, ArrowClockwise,
} from "@phosphor-icons/react";
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Sidebar } from "@/components/Sidebar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

const MONTHS = ["Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil", "Août", "Sep", "Oct", "Nov", "Déc"];
const MONTHS_FULL = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
const eur = (n) => new Intl.NumberFormat("fr-BE", { style: "currency", currency: "EUR" }).format(n || 0);
const todayIso = () => new Date().toISOString().slice(0, 10);

const KpiCard = ({ label, value, icon: Icon, accent, hint }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="flex items-center gap-2 text-slate-500 text-sm">
      <Icon size={18} weight="bold" className={accent} />
      {label}
    </div>
    <p className={`mt-2 text-2xl font-heading font-semibold ${accent}`}>{value}</p>
    {hint && <p className="text-xs text-slate-400 mt-1">{hint}</p>}
  </div>
);

export default function Budget() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [tab, setTab] = useState("dashboard");
  const [summary, setSummary] = useState(null);
  const [cats, setCats] = useState([]);
  const [txs, setTxs] = useState([]);
  const [loading, setLoading] = useState(true);

  // filters for operations
  const [txKind, setTxKind] = useState("all");
  const [txSearch, setTxSearch] = useState("");
  const [uncatOnly, setUncatOnly] = useState(false);

  // dialogs
  const [importOpen, setImportOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);

  const loadSummary = useCallback(async () => {
    try {
      const r = await api.get("/budget/summary", { params: { year } });
      setSummary(r.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }, [year]);

  const loadCats = useCallback(async () => {
    try {
      const r = await api.get("/budget/categories");
      setCats(r.data);
    } catch (err) { /* noop */ }
  }, []);

  const loadTxs = useCallback(async () => {
    try {
      const params = { year };
      if (txKind !== "all") params.kind = txKind;
      if (txSearch.trim()) params.search = txSearch.trim();
      if (uncatOnly) params.uncategorized = true;
      const r = await api.get("/budget/transactions", { params });
      setTxs(r.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }, [year, txKind, txSearch, uncatOnly]);

  useEffect(() => {
    setLoading(true);
    Promise.all([loadSummary(), loadCats()]).finally(() => setLoading(false));
  }, [loadSummary, loadCats]);

  useEffect(() => { if (tab === "operations") loadTxs(); }, [tab, loadTxs]);

  const years = [];
  for (let y = now.getFullYear() + 1; y >= now.getFullYear() - 4; y--) years.push(y);

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar />
      <main className="ml-64 p-8" data-testid="budget-page">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-md bg-blue-600 flex items-center justify-center">
                <Wallet size={20} weight="bold" className="text-white" />
              </div>
              <h1 className="text-2xl font-heading font-semibold text-slate-900">Comptabilité budgétaire</h1>
            </div>
            <p className="text-sm text-slate-500 mt-1">
              Budget annuel réparti par mois · suivi des écarts et cumuls · import des opérations CBC
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger className="w-28" data-testid="budget-year-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex gap-1 border-b border-slate-200 mb-6">
          {[
            { k: "dashboard", label: "Tableau de bord" },
            { k: "operations", label: "Opérations" },
            { k: "categories", label: "Rubriques" },
          ].map((t) => (
            <button key={t.k} data-testid={`budget-tab-${t.k}`}
              onClick={() => setTab(t.k)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                tab === t.k ? "border-blue-600 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-800"
              }`}>
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-slate-500" data-testid="budget-loading">Chargement…</p>
        ) : (
          <>
            {tab === "dashboard" && (
              <DashboardTab summary={summary} month={month} setMonth={setMonth} />
            )}
            {tab === "operations" && (
              <OperationsTab
                txs={txs} cats={cats} reload={() => { loadTxs(); loadSummary(); }}
                txKind={txKind} setTxKind={setTxKind} txSearch={txSearch} setTxSearch={setTxSearch}
                uncatOnly={uncatOnly} setUncatOnly={setUncatOnly}
                onImport={() => setImportOpen(true)} onManual={() => setManualOpen(true)}
              />
            )}
            {tab === "categories" && (
              <CategoriesTab cats={cats} reload={() => { loadCats(); loadSummary(); }} />
            )}
          </>
        )}

        <ImportDialog open={importOpen} setOpen={setImportOpen} cats={cats}
          onDone={() => { setImportOpen(false); loadSummary(); loadTxs(); }} />
        <ManualDialog open={manualOpen} setOpen={setManualOpen} cats={cats}
          onDone={() => { setManualOpen(false); loadSummary(); loadTxs(); }} />
      </main>
    </div>
  );
}

/* ------------------------------------------------------ Dashboard */
function DashboardTab({ summary, month, setMonth }) {
  if (!summary) return null;
  const cm = summary.current_month || 12;
  const sumUpTo = (arr, upto) => arr.slice(0, upto).reduce((a, b) => a + b, 0);

  // KPI: cumul up to current month
  const totExpSpent = summary.expense_categories.reduce((a, c) => a + sumUpTo(c.months, cm), 0);
  const totIncActual = summary.income_categories.reduce((a, c) => a + sumUpTo(c.months, cm), 0);
  const balance = totIncActual - totExpSpent;
  const totExpBudgetY = summary.expense_categories.reduce((a, c) => a + c.annual_budget, 0);
  const totIncBudgetY = summary.income_categories.reduce((a, c) => a + c.annual_budget, 0);

  // alerts: expense cumul over cumul budget
  const alerts = summary.expense_categories
    .map((c) => {
      const spent = sumUpTo(c.months, cm);
      const budget = c.monthly_budget * cm;
      return { name: c.name, spent, budget, over: budget > 0 && spent > budget };
    })
    .filter((a) => a.over)
    .sort((a, b) => (b.spent - b.budget) - (a.spent - a.budget));

  // chart
  const chartData = MONTHS.map((m, i) => ({
    mois: m,
    Dépenses: Math.round(summary.expense_categories.reduce((a, c) => a + c.months[i], 0)),
    Recettes: Math.round(summary.income_categories.reduce((a, c) => a + c.months[i], 0)),
    "Budget dépenses": Math.round(summary.expense_categories.reduce((a, c) => a + c.monthly_budget, 0)),
  }));

  return (
    <div className="space-y-6" data-testid="budget-dashboard">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Recettes (cumul)" value={eur(totIncActual)} icon={TrendUp} accent="text-emerald-600"
          hint={`Budget annuel ${eur(totIncBudgetY)}`} />
        <KpiCard label="Dépenses (cumul)" value={eur(totExpSpent)} icon={TrendDown} accent="text-rose-600"
          hint={`Budget annuel ${eur(totExpBudgetY)}`} />
        <KpiCard label="Solde" value={eur(balance)} icon={Scales}
          accent={balance >= 0 ? "text-emerald-600" : "text-rose-600"}
          hint={`Au ${cm ? MONTHS_FULL[cm - 1] : "—"} ${summary.year}`} />
        <KpiCard label="Non imputé" value={summary.uncategorized_count}
          icon={Warning} accent={summary.uncategorized_count > 0 ? "text-amber-600" : "text-slate-500"}
          hint="opérations à classer" />
      </div>

      {alerts.length > 0 && (
        <div className="bg-rose-50 border border-rose-200 rounded-xl p-4" data-testid="budget-alerts">
          <div className="flex items-center gap-2 text-rose-700 font-medium text-sm mb-2">
            <Warning size={18} weight="fill" /> Dépassements de budget (cumul depuis janvier)
          </div>
          <div className="flex flex-wrap gap-2">
            {alerts.map((a) => (
              <span key={a.name} className="text-xs bg-white border border-rose-200 text-rose-700 rounded-lg px-2.5 py-1"
                data-testid={`budget-alert-item`}>
                <strong>{a.name}</strong> : {eur(a.spent)} / {eur(a.budget)} (+{eur(a.spent - a.budget)})
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center gap-2 mb-4">
          <ChartBar size={18} weight="bold" className="text-blue-600" />
          <h2 className="font-heading font-semibold text-slate-800">Évolution mensuelle {summary.year}</h2>
        </div>
        <ResponsiveContainer width="100%" height={280}>
          <ComposedChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="mois" tick={{ fontSize: 12 }} stroke="#94a3b8" />
            <YAxis tick={{ fontSize: 12 }} stroke="#94a3b8" />
            <Tooltip formatter={(v) => eur(v)} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="Recettes" fill="#10b981" radius={[4, 4, 0, 0]} />
            <Bar dataKey="Dépenses" fill="#f43f5e" radius={[4, 4, 0, 0]} />
            <Line type="monotone" dataKey="Budget dépenses" stroke="#3b82f6" strokeWidth={2} dot={false} strokeDasharray="5 4" />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm text-slate-500 mr-1">Détail du mois :</span>
        {MONTHS.map((m, i) => (
          <button key={m} data-testid={`budget-month-${i + 1}`}
            onClick={() => setMonth(i + 1)}
            className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
              month === i + 1 ? "bg-blue-600 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-100"
            }`}>{m}</button>
        ))}
      </div>

      <BudgetTable title="Dépenses" kind="expense" categories={summary.expense_categories} month={month} />
      <BudgetTable title="Recettes" kind="income" categories={summary.income_categories} month={month} />
    </div>
  );
}

function BudgetTable({ title, kind, categories, month }) {
  const sumUpTo = (arr, upto) => arr.slice(0, upto).reduce((a, b) => a + b, 0);
  const rows = categories.map((c) => {
    const spentM = c.months[month - 1] || 0;
    const budgetM = c.monthly_budget;
    const cumSpent = sumUpTo(c.months, month);
    const cumBudget = c.monthly_budget * month;
    // écart: for expense positive = sous budget (bien); for income positive = au-dessus (bien)
    const ecartM = kind === "expense" ? budgetM - spentM : spentM - budgetM;
    const ecartCum = kind === "expense" ? cumBudget - cumSpent : cumSpent - cumBudget;
    return { ...c, spentM, budgetM, cumSpent, cumBudget, ecartM, ecartCum };
  });
  const tot = rows.reduce((a, r) => ({
    budgetM: a.budgetM + r.budgetM, spentM: a.spentM + r.spentM,
    cumSpent: a.cumSpent + r.cumSpent, cumBudget: a.cumBudget + r.cumBudget,
  }), { budgetM: 0, spentM: 0, cumSpent: 0, cumBudget: 0 });
  const totEcartM = kind === "expense" ? tot.budgetM - tot.spentM : tot.spentM - tot.budgetM;
  const totEcartCum = kind === "expense" ? tot.cumBudget - tot.cumSpent : tot.cumSpent - tot.cumBudget;
  const ecartCls = (v) => (v >= 0 ? "text-emerald-600" : "text-rose-600");

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={`budget-table-${kind}`}>
      <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2">
        {kind === "expense" ? <TrendDown size={18} className="text-rose-500" weight="bold" />
          : <TrendUp size={18} className="text-emerald-500" weight="bold" />}
        <h3 className="font-heading font-semibold text-slate-800">{title}</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-slate-500 text-xs">
              <th className="text-left font-medium px-5 py-2.5">Rubrique</th>
              <th className="text-right font-medium px-3 py-2.5">Budget / mois</th>
              <th className="text-right font-medium px-3 py-2.5">Réel / mois</th>
              <th className="text-right font-medium px-3 py-2.5">Écart mois</th>
              <th className="text-right font-medium px-3 py-2.5 border-l border-slate-200">Cumul réel</th>
              <th className="text-right font-medium px-3 py-2.5">Cumul budget</th>
              <th className="text-right font-medium px-5 py-2.5">Écart cumul</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`budget-row-${r.id}`}>
                <td className="px-5 py-2.5 text-slate-800">{r.name}</td>
                <td className="px-3 py-2.5 text-right text-slate-500">{eur(r.budgetM)}</td>
                <td className="px-3 py-2.5 text-right font-medium text-slate-800">{eur(r.spentM)}</td>
                <td className={`px-3 py-2.5 text-right font-medium ${ecartCls(r.ecartM)}`}>{eur(r.ecartM)}</td>
                <td className="px-3 py-2.5 text-right font-medium text-slate-800 border-l border-slate-200">{eur(r.cumSpent)}</td>
                <td className="px-3 py-2.5 text-right text-slate-500">{eur(r.cumBudget)}</td>
                <td className={`px-5 py-2.5 text-right font-medium ${ecartCls(r.ecartCum)}`}>{eur(r.ecartCum)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-slate-200 bg-slate-50 font-semibold text-slate-800">
              <td className="px-5 py-2.5">Total</td>
              <td className="px-3 py-2.5 text-right">{eur(tot.budgetM)}</td>
              <td className="px-3 py-2.5 text-right">{eur(tot.spentM)}</td>
              <td className={`px-3 py-2.5 text-right ${ecartCls(totEcartM)}`}>{eur(totEcartM)}</td>
              <td className="px-3 py-2.5 text-right border-l border-slate-200">{eur(tot.cumSpent)}</td>
              <td className="px-3 py-2.5 text-right">{eur(tot.cumBudget)}</td>
              <td className={`px-5 py-2.5 text-right ${ecartCls(totEcartCum)}`}>{eur(totEcartCum)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

/* ------------------------------------------------------ Operations */
function OperationsTab({ txs, cats, reload, txKind, setTxKind, txSearch, setTxSearch, uncatOnly, setUncatOnly, onImport, onManual }) {
  const assign = async (tx, cid) => {
    try {
      await api.patch(`/budget/transactions/${tx.id}`, { category_id: cid, remember: true });
      toast.success("Rubrique imputée");
      reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  const del = async (tx) => {
    if (!window.confirm("Supprimer cette opération ?")) return;
    try {
      await api.delete(`/budget/transactions/${tx.id}`);
      reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-4" data-testid="budget-operations">
      <div className="flex items-center gap-2 flex-wrap">
        <Input placeholder="Rechercher un libellé…" value={txSearch}
          onChange={(e) => setTxSearch(e.target.value)} className="w-56" data-testid="budget-search" />
        <Select value={txKind} onValueChange={setTxKind}>
          <SelectTrigger className="w-40" data-testid="budget-kind-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tout</SelectItem>
            <SelectItem value="expense">Dépenses</SelectItem>
            <SelectItem value="income">Recettes</SelectItem>
          </SelectContent>
        </Select>
        <button onClick={() => setUncatOnly(!uncatOnly)} data-testid="budget-uncat-toggle"
          className={`px-3 py-2 rounded-md text-sm border transition-colors ${
            uncatOnly ? "bg-amber-100 border-amber-300 text-amber-700" : "bg-white border-slate-200 text-slate-600"
          }`}>Non imputés</button>
        <div className="flex-1" />
        <Button variant="outline" onClick={onImport} data-testid="budget-import-btn">
          <UploadSimple size={16} className="mr-1.5" /> Importer CBC
        </Button>
        <Button onClick={onManual} data-testid="budget-manual-btn">
          <Plus size={16} className="mr-1.5" /> Opération
        </Button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-slate-500 text-xs">
                <th className="text-left font-medium px-4 py-2.5">Date</th>
                <th className="text-left font-medium px-4 py-2.5">Libellé</th>
                <th className="text-right font-medium px-4 py-2.5">Montant</th>
                <th className="text-left font-medium px-4 py-2.5">Rubrique</th>
                <th className="px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {txs.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400" data-testid="budget-empty">
                  Aucune opération. Importez vos extraits CBC ou ajoutez une opération.
                </td></tr>
              )}
              {txs.map((tx) => (
                <tr key={tx.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`budget-tx-${tx.id}`}>
                  <td className="px-4 py-2.5 text-slate-500 whitespace-nowrap">{tx.date}</td>
                  <td className="px-4 py-2.5 text-slate-800 max-w-md truncate" title={tx.label}>{tx.label}</td>
                  <td className={`px-4 py-2.5 text-right font-medium whitespace-nowrap ${tx.kind === "expense" ? "text-rose-600" : "text-emerald-600"}`}>
                    {tx.kind === "expense" ? "-" : "+"}{eur(tx.amount)}
                  </td>
                  <td className="px-4 py-2.5">
                    <Select value={tx.category_id || "none"} onValueChange={(v) => assign(tx, v === "none" ? null : v)}>
                      <SelectTrigger className={`h-8 w-52 text-xs ${!tx.category_id ? "border-amber-300 bg-amber-50" : ""}`}
                        data-testid={`budget-tx-cat-${tx.id}`}>
                        <SelectValue placeholder="Non imputé" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Non imputé</SelectItem>
                        {cats.filter((c) => c.kind === tx.kind).map((c) => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <button onClick={() => del(tx)} data-testid={`budget-tx-del-${tx.id}`}
                      className="text-slate-400 hover:text-rose-600 transition-colors">
                      <Trash size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------ Categories */
function CategoriesTab({ cats, reload }) {
  const expense = cats.filter((c) => c.kind === "expense");
  const income = cats.filter((c) => c.kind === "income");
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="budget-categories">
      <CategoryList title="Dépenses" kind="expense" items={expense} reload={reload} />
      <CategoryList title="Recettes" kind="income" items={income} reload={reload} />
    </div>
  );
}

function CategoryList({ title, kind, items, reload }) {
  const [newName, setNewName] = useState("");
  const [newBudget, setNewBudget] = useState("");

  const add = async () => {
    if (!newName.trim()) return;
    try {
      await api.post("/budget/categories", { name: newName.trim(), kind, annual_budget: Number(newBudget) || 0 });
      setNewName(""); setNewBudget(""); reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2">
        {kind === "expense" ? <TrendDown size={18} className="text-rose-500" weight="bold" />
          : <TrendUp size={18} className="text-emerald-500" weight="bold" />}
        <h3 className="font-heading font-semibold text-slate-800">{title}</h3>
      </div>
      <div className="divide-y divide-slate-100">
        {items.map((c) => <CategoryRow key={c.id} cat={c} reload={reload} />)}
      </div>
      <div className="p-4 bg-slate-50 border-t border-slate-200 flex items-end gap-2">
        <div className="flex-1">
          <label className="text-xs text-slate-500">Nouvelle rubrique</label>
          <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Nom"
            className="mt-1" data-testid={`budget-newcat-name-${kind}`} />
        </div>
        <div className="w-36">
          <label className="text-xs text-slate-500">Budget annuel €</label>
          <Input type="number" value={newBudget} onChange={(e) => setNewBudget(e.target.value)}
            placeholder="0" className="mt-1" data-testid={`budget-newcat-budget-${kind}`} />
        </div>
        <Button onClick={add} data-testid={`budget-newcat-add-${kind}`}><Plus size={16} /></Button>
      </div>
    </div>
  );
}

function CategoryRow({ cat, reload }) {
  const [name, setName] = useState(cat.name);
  const [budget, setBudget] = useState(cat.annual_budget);
  const dirty = name !== cat.name || Number(budget) !== cat.annual_budget;

  useEffect(() => { setName(cat.name); setBudget(cat.annual_budget); }, [cat.id]);

  const save = async () => {
    try {
      await api.patch(`/budget/categories/${cat.id}`, { name: name.trim(), annual_budget: Number(budget) || 0 });
      toast.success("Rubrique mise à jour");
      reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  const del = async () => {
    if (!window.confirm(`Supprimer la rubrique « ${cat.name} » ? Les opérations liées seront « non imputées ».`)) return;
    try {
      await api.delete(`/budget/categories/${cat.id}`);
      reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="flex items-center gap-2 px-4 py-2.5" data-testid={`budget-cat-${cat.id}`}>
      <Input value={name} onChange={(e) => setName(e.target.value)} className="flex-1 h-9"
        data-testid={`budget-cat-name-${cat.id}`} />
      <div className="w-32">
        <Input type="number" value={budget} onChange={(e) => setBudget(e.target.value)} className="h-9 text-right"
          data-testid={`budget-cat-budget-${cat.id}`} />
      </div>
      <span className="w-24 text-right text-xs text-slate-400">{eur((Number(budget) || 0) / 12)}/mois</span>
      <Button size="sm" variant={dirty ? "default" : "outline"} onClick={save} disabled={!dirty}
        data-testid={`budget-cat-save-${cat.id}`}><FloppyDisk size={15} /></Button>
      <button onClick={del} className="text-slate-400 hover:text-rose-600 p-1.5" data-testid={`budget-cat-del-${cat.id}`}>
        <Trash size={16} />
      </button>
    </div>
  );
}

/* ------------------------------------------------------ Manual dialog */
function ManualDialog({ open, setOpen, cats, onDone }) {
  const [form, setForm] = useState({ date: todayIso(), label: "", amount: "", kind: "expense", category_id: "none" });
  useEffect(() => { if (open) setForm({ date: todayIso(), label: "", amount: "", kind: "expense", category_id: "none" }); }, [open]);

  const submit = async () => {
    if (!form.date || !form.amount) { toast.error("Date et montant requis"); return; }
    try {
      await api.post("/budget/transactions", {
        date: form.date, label: form.label || "Opération", amount: Number(form.amount),
        kind: form.kind, category_id: form.category_id === "none" ? null : form.category_id,
      });
      toast.success("Opération ajoutée");
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="budget-manual-dialog">
        <DialogHeader>
          <DialogTitle>Nouvelle opération</DialogTitle>
          <DialogDescription>Ajout manuel d'une dépense ou d'une recette.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-500">Type</label>
              <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v, category_id: "none" })}>
                <SelectTrigger className="mt-1" data-testid="budget-manual-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="expense">Dépense</SelectItem>
                  <SelectItem value="income">Recette</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-slate-500">Date</label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                className="mt-1" data-testid="budget-manual-date" />
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-500">Libellé</label>
            <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
              className="mt-1" placeholder="Ex : Total carburant" data-testid="budget-manual-label" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-500">Montant €</label>
              <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}
                className="mt-1" placeholder="0,00" data-testid="budget-manual-amount" />
            </div>
            <div>
              <label className="text-xs text-slate-500">Rubrique</label>
              <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v })}>
                <SelectTrigger className="mt-1" data-testid="budget-manual-cat"><SelectValue placeholder="Non imputé" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Non imputé</SelectItem>
                  {cats.filter((c) => c.kind === form.kind).map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
          <Button onClick={submit} data-testid="budget-manual-submit">Ajouter</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------ Import dialog */
function ImportDialog({ open, setOpen, cats, onDone }) {
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [rows, setRows] = useState([]);
  const [batch, setBatch] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setPreview(null); setRows([]); setBatch(""); } }, [open]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/budget/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(r.data);
      setRows(r.data.rows.map((row) => ({ ...row, _include: !row.duplicate })));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const confirm = async () => {
    const selected = rows.filter((r) => r._include);
    if (selected.length === 0) { toast.error("Aucune opération sélectionnée"); return; }
    setBusy(true);
    try {
      const r = await api.post("/budget/import/confirm", {
        batch_label: batch || null,
        rows: selected.map((s) => ({ date: s.date, label: s.label, amount: s.amount, kind: s.kind, category_id: s.category_id || null })),
      });
      toast.success(`${r.data.inserted} opération(s) importée(s)`);
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const setRow = (i, patch) => setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const selCount = rows.filter((r) => r._include).length;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col" data-testid="budget-import-dialog">
        <DialogHeader>
          <DialogTitle>Importer les opérations CBC</DialogTitle>
          <DialogDescription>
            Depuis CBC Touch / CBC Online, exportez vos opérations en CSV (ou Excel) puis chargez le fichier ici.
            Les dépenses (montants négatifs) et recettes (positifs) sont détectées automatiquement.
          </DialogDescription>
        </DialogHeader>

        {!preview ? (
          <div className="py-8">
            <input ref={fileRef} type="file" accept=".csv,.txt,.xlsx,.xls" onChange={onFile}
              className="hidden" data-testid="budget-import-file" />
            <button onClick={() => fileRef.current?.click()} disabled={busy}
              className="w-full border-2 border-dashed border-slate-300 rounded-xl py-12 flex flex-col items-center gap-2 text-slate-500 hover:border-blue-400 hover:text-blue-600 transition-colors"
              data-testid="budget-import-drop">
              {busy ? <ArrowClockwise size={28} className="animate-spin" /> : <UploadSimple size={28} />}
              <span className="text-sm font-medium">{busy ? "Analyse…" : "Choisir un fichier CSV / Excel"}</span>
              <span className="text-xs">CBC · export d'opérations</span>
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3 text-sm py-1">
              <span className="text-slate-600">{preview.count} opérations · </span>
              <span className="text-emerald-600 font-medium">{preview.new} nouvelles</span>
              {preview.duplicates > 0 && <span className="text-amber-600 font-medium">{preview.duplicates} doublons</span>}
              <div className="flex-1" />
              <Input value={batch} onChange={(e) => setBatch(e.target.value)} placeholder="Étiquette de l'import (optionnel)"
                className="w-64 h-8 text-xs" data-testid="budget-import-batch" />
            </div>
            <div className="flex-1 overflow-y-auto border border-slate-200 rounded-lg">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-slate-50">
                  <tr className="text-slate-500">
                    <th className="px-2 py-2 w-8"></th>
                    <th className="text-left px-2 py-2">Date</th>
                    <th className="text-left px-2 py-2">Libellé</th>
                    <th className="text-right px-2 py-2">Montant</th>
                    <th className="text-left px-2 py-2">Rubrique</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} className={`border-t border-slate-100 ${r.duplicate ? "bg-amber-50" : ""}`}
                      data-testid={`budget-import-row-${i}`}>
                      <td className="px-2 py-1.5 text-center">
                        <input type="checkbox" checked={r._include} onChange={(e) => setRow(i, { _include: e.target.checked })}
                          data-testid={`budget-import-check-${i}`} />
                      </td>
                      <td className="px-2 py-1.5 text-slate-500 whitespace-nowrap">{r.date || "?"}</td>
                      <td className="px-2 py-1.5 text-slate-700 max-w-xs truncate" title={r.label}>
                        {r.label}
                        {r.duplicate && <span className="ml-1 text-amber-600">(doublon)</span>}
                      </td>
                      <td className={`px-2 py-1.5 text-right font-medium whitespace-nowrap ${r.kind === "expense" ? "text-rose-600" : "text-emerald-600"}`}>
                        {r.kind === "expense" ? "-" : "+"}{eur(r.amount)}
                      </td>
                      <td className="px-2 py-1.5">
                        <Select value={r.category_id || "none"} onValueChange={(v) => setRow(i, { category_id: v === "none" ? null : v })}>
                          <SelectTrigger className="h-7 w-44 text-xs" data-testid={`budget-import-cat-${i}`}>
                            <SelectValue placeholder="Non imputé" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Non imputé</SelectItem>
                            {cats.filter((c) => c.kind === r.kind).map((c) => (
                              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPreview(null)}>Choisir un autre fichier</Button>
              <Button onClick={confirm} disabled={busy || selCount === 0} data-testid="budget-import-confirm">
                Importer {selCount} opération(s)
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
